import WasmController from "react-lib/frameworks/WasmController";

import axios from "axios";
import CONFIG from "config/config";
import moment from "moment";

// APIs
import CentralLabResultCreateList from "issara-sdk/apis/CentralLabResultCreateList_apps_LAB";
import OCRLabDetail from "issara-sdk/apis/OCRLabDetail_apps_LABM";
import CentralLabResultByOrderGroupbyTestList from "issara-sdk/apis/CentralLabResultByOrderGroupbyTestList_apps_LAB";
import CentralLabSummaryFileResultList from "issara-sdk/apis/CentralLabSummaryFileResultList_apps_LAB";
import CentralLabSummaryFileResultListList from "issara-sdk/apis/CentralLabSummaryFileResultListList_apps_LAB";
import CentralLabSummaryFileResultDetail from "issara-sdk/apis/CentralLabSummaryFileResultDetail_apps_LAB";
import UserEmployeeDetailAPIView from "issara-sdk/apis/UserEmployeeDetailAPIView_users";
import PackageOrderEncounterMapList from "issara-sdk/apis/PackageOrderEncounterMapList_apps_APP";
import AIChatFastAPIRequestView from "issara-sdk/apis/AIChatFastAPIRequestViewM";
import CentralLabOrderDetail from "issara-sdk/apis/CentralLabOrderDetail_apps_LAB";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";
import { HandleGetEmployeeTokenization } from "react-lib/apps/HISV3/TPD/TPDInterface";
import { SetErrorMessage, GetOALogoBase64 } from "../../common/CommonInterface";

// Form
import FormLabExamSummary from "../FormLabExamSummary";
import FormLaboratoryTestResult from "../FormLaboratoryTestResult";
import FormMeLabResult, { FormMeLabResultProps } from "../FormMeLabResult";

// Utils
import { convertAdBeDateToMoment, formatDate } from "react-lib/utils/dateUtils";
import getPdfMake from "react-lib/appcon/common/pdfMake";

export type Props = {};

export type State = Partial<{
  LabReportSequence: Partial<{
    sequenceIndex: string | null;
    selectedStartDate: string | null;
    selectedEndDate: string | null;
    selectedLabDivision: string | null;
    selectedLabTest: string | null;
    centralLabResult: {
      id?: number | null;
      items?: any[];
      package_order_id?: any[];
    };
    LabReportToken: {
      token?: string;
      employeeName?: string;
      error?: any;
      loading?: boolean;
      code?: string;
      password?: string;
    };
    LabReportList: any[];
    selectedLabReport: any;
    // report ของ lab order ที่เปิดอยู่ — undefined = ยังไม่ได้ fetch, null = ยังไม่มี report
    meLabReport: MeLabReportSummary | null;
    // status แบบ real-time ราย report — key ด้วย report_id
    meLabVideoStatus: Record<string, MeLabVideoStatus>;
    // สถานะสไลด์ราย report — key ด้วย report_id
    meLabPptxStatus: Record<string, MeLabPptxStatus>;
  }> | null;
  selectedLabOrderWorking: any;
}>;

export type MeLabReportSummary = {
  report_id: string;
  report_name: string;
  created_at: string;
  patient_name: string | null;
  central_lab_order_id: string | null;
  overall_status: string; // "completed" | "draft" | "failed" — ของ report/OCR ไม่ใช่ของ video
  video_output_url: string | null; // มีค่า = gen video เสร็จแล้ว
  approved_at: string | null; // มีค่า = อนุมัติ video แล้ว
  approved_by: string | null;
  pdf_output_url: string | null; // มีค่า = gen PDF เสร็จแล้ว
  pdf_approved_at: string | null; // มีค่า = อนุมัติ PDF แล้ว
  pdf_approved_by: string | null;
};

// หนึ่ง section ใน PDF — ส่วนเดียวที่เปิดให้เจ้าหน้าที่แก้ได้
export type MeLabPdfSection = {
  section_title: string;
  detail_text: string;
};

export const VIDEO_STATUS_NOT_FOUND = "not_found";

export type MeLabVideoStatus = {
  status: string;
  current_step: string | null;
  current_step_detail: string | null;
  // ปลายทางฝั่ง MeGen ของ lip sync ที่ v5 สร้าง
  megen_project_id: number | null;
  // เป็น "pending" ได้ระหว่างที่ยังผูก run ไม่เสร็จ
  thread_id: string | null;
};

export const PPTX_STATUS_NEEDS_REVIEW = "needsReview";

export type MeLabPptxStatus = {
  generated: boolean;
  pptx_output_url: string | null;
  pptx_generated_by: string | null;
  pptx_generated_by_name: string | null;
  pptx_generated_at: string | null;
  pptx_approved_by: string | null;
  pptx_approved_by_name: string | null;
  pptx_approved_at: string | null;
  labmap_formula_id: string | null;
  labmap_formula_version: string | null;
  labmap_status: string | null; // ready | needsReview | incomplete
};

type DivisionGroup = {
  id: number;
  name: string;
  display_seq: number;
  code: string;
  synonym: string;
  is_active: boolean;
};

type Picked = Partial<
  Pick<
    MainState,
    | "selectedPatient"
    | "selectedEncounter"
    | "errorMessage"
    | "buttonLoadCheck"
    | "successMessage"
    | "labOrderQueue"
  >
>;

// Sequence
type SeqState = {
  sequence: "LabReport";
  restart?: boolean;
  clear?: boolean;
};
// Handle Action
type ActionType =
  | {
      action: "attach_file";
      card: string;
      buttonLoadKey: string;
      upload: { result: any }[];
      remove: number[];
      date: string;
      time: string;
      labOrderItems: any[];
      password: string;
    }
  | {
      action: "EDIT" | "AUTHORIZE";
      card: string;
      buttonLoadKey: string;
      labOrderItems: any[];
      username?: string;
      password: string;
      // callback
      onUpdated?: () => any;
    }
  | { action: "token"; code: string }
  | { action: "FETCH_LAB_SUMMARY_FILE_RESULT"; btnKey: string; params: any }
  | { action: "select_report"; data: any }
  | { action: "PRINT_LAB_REPORT"; data: any }
  | {
      action: "PRINT_LAB_RESULT";
      card: string;
      data: any;
      packageOrderId: string;
      selectedLabOrder: any;
      selectedPatient: any;
    }
  | {
      action: "SAVE_COMMENT";
      card: string;
      data: Record<string, any>;
      value: string;
      onSuccess?: () => void;
    }
  | {
      action: "OCR_UPLOAD";
      card: string;
      buttonLoadKey: string;
      file: File;
      patient?: number;
    }
  | {
      // v5 ต่างจาก v4 แค่ endpoint (2 scene ต่อ rule + สร้าง lip sync project ที่ MeGen) params เหมือนกัน
      action: "GENERATE_MELAB_VIDEO" | "GENERATE_MELAB_VIDEO_V5";
      card: string;
      buttonLoadKey: string;
      labOrdersReported: any[];
      patientInfo?: Record<string, any>;
      selectedLabOrderWorking?: Record<string, any>;
      // ใช้ report เดิมถ้ามี ไม่งั้นจะสร้าง report ซ้ำทุกครั้งที่กด
      reportId?: string;
      // สร้างทับของเดิม — ไม่ส่งจะได้ของเก่ากลับมา
      forceNew?: boolean;
      // v5 เท่านั้น: ทำแค่เสียงรายฉาก ข้าม merge เพื่อให้ได้ฟังเร็ว แล้วไป render ต่อที่ MeGen
      audioOnly?: boolean;
    }
  | {
      action: "GENERATE_MELAB_PDF";
      card: string;
      buttonLoadKey: string;
      labOrdersReported: any[];
      patientInfo?: Record<string, any>;
      selectedLabOrderWorking?: Record<string, any>;
      reportId?: string;
    }
  | {
      // สร้าง report ใบใหม่จากรายการที่ติ๊กไว้ แล้ว gen ทั้ง video และ PDF ต่อให้เลย
      action: "CREATE_MELAB_REPORT_SET";
      card: string;
      buttonLoadKey: string;
      labOrdersReported: any[];
      patientInfo?: Record<string, any>;
      selectedLabOrderWorking?: Record<string, any>;
      onSuccess?: () => void;
    }
  | {
      action: "GENERATE_MELAB_PPTX";
      card: string;
      buttonLoadKey: string;
      labOrdersReported: any[];
      patientInfo?: Record<string, any>;
      selectedLabOrderWorking?: Record<string, any>;
      reportId?: string;
      // สร้างแบบร่างแม้ข้อมูลไม่ครบหรือขัดกัน
      allowNeedsReview?: boolean;
      onSuccess?: () => void;
      onNeedsReview?: (reasons: string[]) => void;
    }
  | { action: "FETCH_MELAB_PPTX_STATUS"; reportId: string; onSettled?: () => void }
  | {
      action: "APPROVE_MELAB_PPTX";
      card: string;
      buttonLoadKey: string;
      reportId: string;
      onSuccess?: () => void;
    }
  | { action: "FETCH_MELAB_REPORT"; centralLabOrderId?: number | string }
  | {
      action: "FETCH_MELAB_PDF_DATA";
      reportId: string;
      onSuccess?: (sections: MeLabPdfSection[]) => void;
      onError?: (err: any) => void;
    }
  | {
      action: "SAVE_MELAB_PDF_DATA";
      card: string;
      buttonLoadKey: string;
      reportId: string;
      ruleSections: MeLabPdfSection[];
      birthdate?: string;
      onSuccess?: () => void;
    }
  | { action: "FETCH_MELAB_VIDEO_STATUS"; reportId: string; onSettled?: () => void }
  | { action: "GOTO_MEGEN_PROJECT"; projectId: number; threadId?: string }
  | {
      action: "APPROVE_MELAB_PDF";
      card: string;
      buttonLoadKey: string;
      reportId: string;
      onSuccess?: () => void;
    }
  | {
      action: "APPROVE_MELAB_VIDEO";
      card: string;
      buttonLoadKey: string;
      reportId: string;
      onSuccess?: () => void;
    }
  | {
      action: "FETCH_MELAB_VIDEO_DOWNLOAD";
      reportId: string;
      onSuccess?: (downloadUrl: string) => void;
      onError?: (err: any) => void;
    }
  | {
      action: "SAVE_RESULT_REMARK";
      card: string;
      buttonLoadKey: string;
      resultRemark?: string;
      onSuccess?: () => void;
    };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => any;

type Params<A extends ActionType["action"]> = Extract<ActionType, { action: A }>;

export const StateInitial: State = {
  LabReportSequence: {
    sequenceIndex: null,
    selectedStartDate: null,
    selectedEndDate: null,
    selectedLabDivision: null,
    selectedLabTest: "",
    centralLabResult: {},
    LabReportToken: {},
    LabReportList: [],
  },
};

export type Event =
  | { message: "RunSequence"; params: {} }
  | { message: "GetMasterData"; params: {} };

export type Data = {
  division?: number;
  masterLabDivisions?: any[];
  userProfile?: Record<string, any>;
  masterData?: Record<string, any>;
};

export const DataInitial = {
  masterLabDivisions: [],
};

type Handler = (
  controller: WasmController<State & Picked, Event, Data, Props>,
  params?: any,
) => any;

export const Start: Handler = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: [["labDivision", {}]],
    },
  } as any);

  const state = controller.getState();

  const centralLab = await GetCentralLabResultByOrderGroupbyTest(controller, {
    id: state.selectedLabOrderWorking?.id,
  });

  controller.setState({
    LabReportSequence: {
      ...state.LabReportSequence,
      LabReportToken: {},
      sequenceIndex: "Action",
      centralLabResult: centralLab?.[0] || {},
    },
  });
};

export const Action: Handler = async (controller, params: ActionType) => {
  const state = controller.getState();

  if (params.action === "token") {
    const [response, error, network] = await HandleGetEmployeeTokenization(controller as any, {
      code: params.code,
    });

    if (error) {
      controller.setState({
        LabReportSequence: {
          ...state.LabReportSequence,
          LabReportToken: { error: error, loading: false },
        },
      });
      return;
    }
    controller.setState({
      LabReportSequence: {
        ...state.LabReportSequence,
        LabReportToken: {
          token: response?.token || "",
          employeeName: decodeURIComponent(atob(response?.token.split(".")[1])) || "",
          loading: false,
          code: params.code,
        },
      },
    });
  } else if (params.action === "attach_file") {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [params.buttonLoadKey]: "LOADING",
      },
    });

    const employeeCode = await GetEmployeeCode(controller, {
      ...params,
      btnKey: params.buttonLoadKey,
      cardKey: params.card,
      isError: true,
      // password: state.LabReportSequence?.LabReportToken?.password
    });

    if (!employeeCode) {
      return;
    }

    const filterItems = params.labOrderItems
      .flatMap((item: any) => (item.children.length ? item.children : [item]))
      .filter((item: any) => item.chk)
      .map((item) => item.central_lab_order_item_id);

    let error = null;

    for (const item of params.upload) {
      const fileResult = await CentralLabSummaryFileResultList.create({
        data: {
          order: state.LabReportSequence?.centralLabResult?.id,
          ref_order_items: filterItems,
          description: "",
          report_pdf: item.result.replace(/^data:application\/pdf;base64,/g, ""),
          employee_code: employeeCode,
          report_datetime: `${params.date}-${params.time}`,
        } as any,
        apiToken: controller.apiToken,
        extra: { division: controller.data.division },
      });

      if (fileResult[1]) {
        error = fileResult[1];
        break;
      }
    }

    if (!error) {
      // หากมี file ที่ต้องการ delete
      const promiseArr = params.remove.map((id) =>
        CentralLabSummaryFileResultDetail.delete({
          pk: id,
          apiToken: controller.apiToken,
          extra: { division: controller.data.division },
        }),
      );

      const response = await Promise.all(promiseArr);

      if (response.some((item) => item[1])) {
        error = response.map((item) => item[1]);
      }
    }

    if (error) {
      controller.setState({
        buttonLoadCheck: {
          ...state.buttonLoadCheck,
          [params.buttonLoadKey]: "ERROR",
        },
        errorMessage: {
          ...state.errorMessage,
          [params.card]: { error: error },
        },
      });
    } else {
      controller.setState(
        {
          successMessage: {
            ...state.successMessage,
            [params?.card]: "บันทึกสำเร็จ",
          },
          errorMessage: {
            ...state.errorMessage,
            [params.card]: { error: null },
          },

          buttonLoadCheck: {
            ...state.buttonLoadCheck,
            [params.buttonLoadKey]: "SUCCESS",
          },
          LabReportSequence: {
            sequenceIndex: "START",
            centralLabResult: state.LabReportSequence?.centralLabResult,
          },
        },
        () =>
          controller.handleEvent({
            message: "RunSequence" as any,
            params: {
              sequence: "LabReport" as any,
            },
          }),
      );
    }
  } else if (params.action === "AUTHORIZE" || params.action === "EDIT") {
    const state = controller.getState();

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [params.buttonLoadKey]: "LOADING",
      },
    });

    const flatMapItems = (items?: any[]) =>
      (items || []).flatMap((item: any) => (item.children.length ? item.children : [item]));

    const rawLabOrderItems = flatMapItems(state.LabReportSequence?.centralLabResult?.items);

    let labOrderItems = flatMapItems(params.labOrderItems);

    labOrderItems = labOrderItems
      .filter((item: any, index) =>
        params.action === "EDIT"
          ? item.central_lab_result_value !== null &&
            item.central_lab_result_value !== rawLabOrderItems[index].central_lab_result_value
          : item.chk,
      )
      .map((item: any) => ({
        id: item.central_lab_result_id,
        comment: item.comment || "",
        order_item: item.central_lab_order_item_id,
        value: item.central_lab_result_value,
      }));

    let saveLabResult: any = [{}, null, null];

    const { userId } = await controller.handleEvent({
      message: "CheckUserCredentials",
      params: {
        ...params,
        btnKey: params.buttonLoadKey,
        cardKey: params.card,
        isError: true,
        password: params.password,
      },
    });

    if (!userId) {
      return;
    }

    if (labOrderItems.length > 0) {
      saveLabResult = await CentralLabResultCreateList.post({
        data: {
          items: labOrderItems,
          action: params.action,
          username: controller.data.userProfile?.username,
          password: params?.password,
        },
        apiToken: controller.apiToken,
      });
    }

    if (saveLabResult[1]) {
      controller.setState({
        buttonLoadCheck: {
          ...state.buttonLoadCheck,
          [params.buttonLoadKey]: "ERROR",
        },
        errorMessage: {
          ...state.errorMessage,
          [params.card]: { error: saveLabResult[1] },
        },
      });
    } else {
      params.onUpdated?.();

      controller.setState(
        {
          successMessage: {
            ...state.successMessage,
            [params.card]: saveLabResult[0],
          },
          errorMessage: {
            ...state.errorMessage,
            [params.card]: { error: null },
          },
          buttonLoadCheck: {
            ...state.buttonLoadCheck,
            [params.buttonLoadKey]: "SUCCESS",
          },
          LabReportSequence: {
            sequenceIndex: "START",
            centralLabResult: state.LabReportSequence?.centralLabResult,
          },
        },
        () =>
          controller.handleEvent({
            message: "RunSequence" as any,
            params: {
              sequence: "LabReport" as any,
            },
          }),
      );
    }
  } else if (params.action === "FETCH_LAB_SUMMARY_FILE_RESULT") {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [params?.btnKey]: "LOADING",
      },
    });

    const [response, error, network] = await CentralLabSummaryFileResultListList.get({
      apiToken: controller.apiToken,
      params: {
        ...params.params,
        start_order_date: state.LabReportSequence?.selectedStartDate,
        end_order_date: state.LabReportSequence?.selectedEndDate,
        lab_division:
          state.LabReportSequence?.selectedLabDivision === "all"
            ? ""
            : state.LabReportSequence?.selectedLabDivision,
        lab_test:
          state.LabReportSequence?.selectedLabTest === "all"
            ? ""
            : state.LabReportSequence?.selectedLabTest,
      },
    });
    if (error) {
      controller.setState({
        buttonLoadCheck: {
          ...state.buttonLoadCheck,
          [params?.btnKey]: "ERROR",
        },
        LabReportSequence: { ...state.LabReportSequence, LabReportList: [] },
      });
    } else {
      controller.setState({
        buttonLoadCheck: {
          ...state.buttonLoadCheck,
          [params?.btnKey]: "SUCCESS",
        },
        LabReportSequence: {
          ...state.LabReportSequence,
          LabReportList: response?.items || [],
        },
      });
    }
  } else if (params.action === "select_report") {
    controller.setState({
      LabReportSequence: {
        ...state.LabReportSequence,
        selectedLabReport: params.data
          ? { ...params.data, summary_file: params.data.summary_file }
          : null,
      },
    });
  } else if (params.action === "PRINT_LAB_REPORT") {
    const state = controller.getState();
    const patient = state?.selectedPatient;

    const data = {
      time: moment().format("HH:mm") || "",
      date: formatDate(moment()) || "",
      patient: patient || {},
      parentLabID: params.data.map((item: any) => item.code),
      labResultList: getGroupByParentLab(params.data),
      hideRecommendation: true,
    };

    const docDef: any = await FormLabExamSummary(data);

    (await getPdfMake()).createPdf(docDef).open();
  } else if (params.action === "PRINT_LAB_RESULT") {
    const btnKey = `${params.card}_${params.action}`;

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
    });

    await HandlePrintLabResult(controller, params);

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "SUCCESS" },
    });
  } else if (params.action === "SAVE_COMMENT") {
    const state = controller.getState();

    const btnKey = `${params.card}_SAVE`;
    const { data, value } = params;

    controller.setState({ buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" } });

    const result = await CentralLabResultCreateList.post({
      apiToken: controller.apiToken,
      data: {
        action: "UPDATE_COMMENT",
        items: [
          {
            id: data.central_lab_result_id,
            comment: value,
            order_item: data.central_lab_order_item_id,
            value: data.central_lab_result_value,
          },
        ],
      },
    });

    if (result[1]) {
      SetErrorMessage(controller, { ...params, action: "SAVE", error: result[1] });

      return;
    }

    controller.setState(
      {
        LabReportSequence: {
          sequenceIndex: "START",
          centralLabResult: state.LabReportSequence?.centralLabResult,
        },
      },
      () => {
        controller.handleEvent({
          message: "RunSequence" as any,
          params: {
            sequence: "LabReport" as any,
          },
        });

        controller.setState({ buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null } });

        params.onSuccess?.();
      },
    );
  } else if (params.action === "OCR_UPLOAD") {
    const state = controller.getState();
    const btnKey = params.buttonLoadKey;

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
      errorMessage: { ...state.errorMessage, [params.card]: null },
    });

    const labOrderId = state.selectedLabOrderWorking?.id;

    const formData = new FormData();
    formData.append("file", params.file);
    formData.append("pdf_password", ""); // **
    if (params.patient) {
      formData.append("patient", String(params.patient));
    }

    const [response, error] = await OCRLabDetail.update({
      pk: labOrderId,
      data: formData,
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    });

    if (error) {
      controller.setState({
        buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
        errorMessage: {
          ...state.errorMessage,
          [params.card]: { error },
        },
      });
      return;
    }

    // Refresh
    const centralLab = await GetCentralLabResultByOrderGroupbyTest(controller, {
      id: labOrderId,
    });

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "SUCCESS" },
      successMessage: {
        ...state.successMessage,
        [params.card]: "OCR Upload สำเร็จ",
      },
      LabReportSequence: {
        ...state.LabReportSequence,
        centralLabResult: centralLab?.[0] || state.LabReportSequence?.centralLabResult,
      },
    });
  } else if (params.action === "GENERATE_MELAB_VIDEO") {
    await GenerateMeLabVideo(controller, params, "v4");
  } else if (params.action === "GENERATE_MELAB_VIDEO_V5") {
    await GenerateMeLabVideo(controller, params, "v5");
  } else if (params.action === "GENERATE_MELAB_PDF") {
    await HandleGenerateMeLabPdf(controller, params);
  } else if (params.action === "FETCH_MELAB_VIDEO_STATUS") {
    await FetchMeLabVideoStatus(controller, params);
  } else if (params.action === "GOTO_MEGEN_PROJECT") {
    HandleGotoMeGenProject(controller, params);
  } else if (params.action === "FETCH_MELAB_PDF_DATA") {
    await FetchMeLabPdfData(controller, params);
  } else if (params.action === "SAVE_MELAB_PDF_DATA") {
    await HandleSaveMeLabPdfData(controller, params);
  } else if (params.action === "CREATE_MELAB_REPORT_SET") {
    await HandleCreateMeLabReportSet(controller, params);
  } else if (params.action === "FETCH_MELAB_REPORT") {
    await FetchMeLabReport(controller, params.centralLabOrderId);
  } else if (params.action === "APPROVE_MELAB_PDF") {
    await HandleApproveMeLabPdf(controller, params);
  } else if (params.action === "APPROVE_MELAB_VIDEO") {
    await HandleApproveMeLabVideo(controller, params);
  } else if (params.action === "GENERATE_MELAB_PPTX") {
    await HandleGenerateMeLabPptx(controller, params);
  } else if (params.action === "FETCH_MELAB_PPTX_STATUS") {
    await FetchMeLabPptxStatus(controller, params);
  } else if (params.action === "APPROVE_MELAB_PPTX") {
    await HandleApproveMeLabPptx(controller, params);
  } else if (params.action === "FETCH_MELAB_VIDEO_DOWNLOAD") {
    await HandleFetchMeLabVideoDownload(controller, params);
  } else if (params.action === "SAVE_RESULT_REMARK") {
    await HandleSaveResultRemark(controller, params);
  }
};

// ======================= MeLab Video Helpers (issue #84784) =======================
// avatar ตั้งต้นของ Virtual Technician
const MELAB_AVATAR_IMAGE_URL =
  "https://megen-api-prod.ishealth.app/static/characters/series_189/char_a7e3ed3d.png";

const mapLabResultsToApi = (labOrdersReported: any[]) =>
  (labOrdersReported || [])
    .flatMap((item: any) => (item.children?.length ? item.children : [item]))
    .filter((src: any) => src.central_lab_result_is_authorized)
    .map((src: any) => ({
      test_name: src.name_en,
      value: src.central_lab_result_value || "",
      unit: src.unit || "",
      reference_range: src.ref_value_txt || "",
      flag: src.result_status || null,
      category: src.lab_division_name || null,
      central_lab_test_id: src.id ? String(src.id) : null,
      central_lab_test_code: src.code || null,
    }));

// รหัสเปิดไฟล์ PDF = เดือน/ปีเกิดคนไข้ mmyyyy (ค.ศ.) — convertAdBeDateToMoment รับได้ทั้ง พ.ศ./ค.ศ.
// export เพราะฝั่ง UI ต้องโชว์รหัสเดียวกันนี้ให้เจ้าหน้าที่กรอกตอนเปิดไฟล์
export const getMeLabPdfLock = (birthdate?: string): string => {
  const parsed = convertAdBeDateToMoment(birthdate);

  return parsed?.isValid() ? parsed.format("MMYYYY") : "";
};

const CreateMeLabReport = async (
  controller: any,
  params:
    | Params<"GENERATE_MELAB_VIDEO">
    | Params<"GENERATE_MELAB_PDF">
    | Params<"GENERATE_MELAB_PPTX">,
): Promise<[string | null, any]> => {
  const state = controller.getState();
  const labCode = params.selectedLabOrderWorking?.full_access_number || "";
  // proxyPatientAdmin.user เก็บใน MeBillingSequence (PatientSerializer ไม่มี field user)
  const proxyPatientUser = (state as any).MeBillingSequence?.selectedCustomer?.proxyPatientAdmin
    ?.user;
  const staffUser = state.django?.user?.id;
  const dateIso = params.selectedLabOrderWorking?.date_iso;
  const reportDate = dateIso ? moment(dateIso).format("YYYY-MM-DD") : "";

  const [createResp, createErr] = await AIChatFastAPIRequestView.post<{
    success: boolean;
    message: string;
    data: { report_id: string };
  }>({
    endpoint: "/medical-ocr/reports/staff-create-with-results",
    apiToken: controller.apiToken,
    data: {
      ...(proxyPatientUser && { user_id_patient: String(proxyPatientUser) }),
      ...(staffUser && { user_id_staff: String(staffUser) }),
      patient_id: String(params.patientInfo?.id ?? ""),
      central_lab_order_id: String(params.selectedLabOrderWorking?.id ?? ""),
      division_id: String(controller.data.division ?? ""),
      report_name: labCode ? `ผลตรวจ Lab ${labCode}` : "ผลตรวจ Lab",
      report_description: "ดึงข้อมูลจากระบบ HIS",
      ...(reportDate && { report_date: reportDate }),
      patient_name: params.patientInfo?.full_name || params.patientInfo?.real_name || "",
      patient_age: params.patientInfo?.age ?? null,
      patient_gender: params.patientInfo?.gender || "",
      lab_results: mapLabResultsToApi(params.labOrdersReported),
    },
  });

  if (createErr) {
    return [null, createErr];
  }

  const reportId = createResp?.data?.report_id;

  return reportId ? [reportId, null] : [null, "สร้าง report สำเร็จแต่ไม่ได้ report_id"];
};

const GetOrCreateMeLabReport = async (
  controller: any,
  params:
    | Params<"GENERATE_MELAB_VIDEO">
    | Params<"GENERATE_MELAB_PDF">
    | Params<"GENERATE_MELAB_PPTX">,
): Promise<string | null> => {
  if (params.reportId) {
    return params.reportId;
  }

  const [createdId, createErr] = await CreateMeLabReport(controller, params);

  if (!createdId) {
    const state = controller.getState();

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [params.buttonLoadKey]: "ERROR" },
      errorMessage: { ...state.errorMessage, [params.card]: { error: createErr } },
    });
  }

  return createdId;
};

const GenerateMeLabVideo = async (
  controller: Parameters<Handler>[0],
  params: Params<"GENERATE_MELAB_VIDEO">,
  version: "v4" | "v5",
) => {
  const { buttonLoadKey: btnKey, card } = params;

  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const reportId = await GetOrCreateMeLabReport(controller, params);

  if (!reportId) {
    return;
  }

  const [, genErr] = await AIChatFastAPIRequestView.post({
    endpoint: `/medical-ocr/reports/${reportId}/melab-video-generate-${version}`,
    params: {
      debug: false,
      avatar_image_url: MELAB_AVATAR_IMAGE_URL,
      force_new: !!params.forceNew,
      ...(params.audioOnly && { audio_only: true, skip_merge: true }),
    },
    apiToken: controller.apiToken,
  });

  state = controller.getState();

  if (genErr) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: { ...state.errorMessage, [card]: { error: genErr } },
    });
    return;
  }

  const { [reportId]: staleStatus, ...otherStatus } = state.LabReportSequence?.meLabVideoStatus || {};

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "SUCCESS" },
    LabReportSequence: {
      ...state.LabReportSequence,
      meLabVideoStatus: otherStatus,
    },
  });

  await FetchMeLabReport(controller, params.selectedLabOrderWorking?.id);
};

// สร้าง report ใบใหม่เสมอ (ไม่ reuse ของเดิม) แล้วสั่ง gen ทั้งสองอย่างด้วย report ใหม่
const HandleCreateMeLabReportSet: Handler = async (controller, params) => {
  const { buttonLoadKey: btnKey, card } = params;
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const fail = (error: any) => {
    const current = controller.getState();

    controller.setState({
      buttonLoadCheck: { ...current.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: { ...current.errorMessage, [card]: { error } },
    });
  };

  const [reportId, createErr] = await CreateMeLabReport(controller, params);

  if (!reportId) {
    fail(createErr);

    return;
  }

  const [, videoErr] = await AIChatFastAPIRequestView.post({
    endpoint: `/medical-ocr/reports/${reportId}/melab-video-generate-v4`,
    params: {
      debug: false,
      avatar_image_url: MELAB_AVATAR_IMAGE_URL,
      force_new: true,
    },
    apiToken: controller.apiToken,
  });

  if (videoErr) {
    fail(videoErr);

    return;
  }

  const pdfLock = getMeLabPdfLock(params.patientInfo?.birthdate);

  const [, pdfErr] = await AIChatFastAPIRequestView.post({
    endpoint: `/medical-ocr/reports/${reportId}/melab-pdf-text-generate-v4`,
    params: {
      debug: false,
      use_v2: true,
      save_to_bucket: true,
      ...(pdfLock && { pdf_lock: pdfLock }),
    },
    apiToken: controller.apiToken,
  });

  if (pdfErr) {
    fail(pdfErr);

    return;
  }

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
    LabReportSequence: { ...state.LabReportSequence, meLabVideoStatus: {} },
  });

  await FetchMeLabReport(controller, params.selectedLabOrderWorking?.id);

  params.onSuccess?.();
};

const HandleGenerateMeLabPdf: Handler = async (controller, params) => {
  const { buttonLoadKey: btnKey, card } = params;
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const reportId = await GetOrCreateMeLabReport(controller, params);

  if (!reportId) {
    return;
  }

  const pdfLock = getMeLabPdfLock(params.patientInfo?.birthdate);

  const [pdfBlob, pdfErr] = await AIChatFastAPIRequestView.post<Blob>({
    endpoint: `/medical-ocr/reports/${reportId}/melab-pdf-text-generate-v4`,
    params: {
      debug: false,
      export_pdf: true,
      use_v2: true,
      save_to_bucket: true,
      ...(pdfLock && { pdf_lock: pdfLock }),
    },
    apiToken: controller.apiToken,
    extra: { responseType: "blob" },
  });

  state = controller.getState();

  if (pdfErr || !pdfBlob?.size) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: {
        ...state.errorMessage,
        [card]: { error: pdfErr || "ไม่ได้รับไฟล์ PDF จากระบบ" },
      },
    });
    return;
  }

  const labCode = params.selectedLabOrderWorking?.full_access_number || "";
  const fileName = labCode ? `ผลตรวจ Lab ${labCode}.pdf` : "ผลตรวจ Lab.pdf";
  const blobUrl = URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));

  const tab = window.open(blobUrl, "_blank");

  if (!tab) {
    const link = document.createElement("a");

    link.href = blobUrl;
    link.download = fileName;
    link.click();
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "SUCCESS" },
  });

  await FetchMeLabReport(controller, params.selectedLabOrderWorking?.id);
};

const FetchMeLabPdfData: Handler = async (controller, params) => {
  const [resp, err] = await AIChatFastAPIRequestView.get<any>({
    endpoint: `/medical-ocr/reports/${params.reportId}/pdf-v4-data`,
    apiToken: controller.apiToken,
  });

  if (err || !resp) {
    params.onError?.(err || "โหลดข้อมูล PDF ไม่สำเร็จ");
    return;
  }

  const data = resp.data;

  params.onSuccess?.(Array.isArray(data?.rule_sections) ? data.rule_sections : []);
};

const HandleSaveMeLabPdfData: Handler = async (controller, params) => {
  const { buttonLoadKey: btnKey, card } = params;
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const pdfLock = getMeLabPdfLock(params.birthdate);

  const [, err] = await AIChatFastAPIRequestView.put({
    endpoint: `/medical-ocr/reports/${params.reportId}/pdf-v4-data`,
    params: { save_to_bucket: true, ...(pdfLock && { pdf_lock: pdfLock }) },
    data: { rule_sections: params.ruleSections },
    apiToken: controller.apiToken,
  });

  state = controller.getState();

  if (err) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: { ...state.errorMessage, [card]: { error: err } },
    });
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "SUCCESS" },
  });

  await FetchMeLabReport(controller, state.selectedLabOrderWorking?.id);

  params.onSuccess?.();
};

const HandleApproveMeLabPdf: Handler = async (controller, params) => {
  const { buttonLoadKey: btnKey, card } = params;
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const [, err] = await AIChatFastAPIRequestView.post({
    endpoint: `/medical-ocr/reports/${params.reportId}/approve-pdf`,
    apiToken: controller.apiToken,
  });

  state = controller.getState();

  if (err) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: { ...state.errorMessage, [card]: { error: err } },
    });
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });

  await FetchMeLabReport(controller, controller.getState().selectedLabOrderWorking?.id);

  params.onSuccess?.();
};

const HandleApproveMeLabVideo: Handler = async (controller, params) => {
  const { buttonLoadKey: btnKey, card } = params;
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const [, err] = await AIChatFastAPIRequestView.post({
    endpoint: `/medical-ocr/reports/${params.reportId}/approve-video`,
    apiToken: controller.apiToken,
  });

  state = controller.getState();

  if (err) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: { ...state.errorMessage, [card]: { error: err } },
    });
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });

  await FetchMeLabReport(controller, controller.getState().selectedLabOrderWorking?.id);

  params.onSuccess?.();
};

const HandleFetchMeLabVideoDownload: Handler = async (controller, params) => {
  const [resp, err] = await AIChatFastAPIRequestView.get<{
    success: boolean;
    message: string;
    data: { report_id: string; thread_id: string; download_url: string };
  }>({
    endpoint: `/medical-ocr/reports/${params.reportId}/melab-video-download`,
    apiToken: controller.apiToken,
  });

  if (err || !resp) {
    params.onError?.(err || "ดึง URL ไม่สำเร็จ");
    return;
  }

  params.onSuccess?.(resp.data.download_url);
};

// ======================= MeLab Slide (PPTX v6) =======================
const normalizeMeLabPptxStatus = (data: any): MeLabPptxStatus => ({
  generated: !!data?.pptx_output_url,
  pptx_output_url: data?.pptx_output_url ?? null,
  pptx_generated_by: data?.pptx_generated_by ?? null,
  pptx_generated_by_name: data?.pptx_generated_by_name ?? null,
  pptx_generated_at: data?.pptx_generated_at ?? null,
  pptx_approved_by: data?.pptx_approved_by ?? null,
  pptx_approved_by_name: data?.pptx_approved_by_name ?? null,
  pptx_approved_at: data?.pptx_approved_at ?? null,
  labmap_formula_id: data?.labmap_formula_id ?? data?.formula?.id ?? null,
  labmap_formula_version: data?.labmap_formula_version ?? data?.formula?.version ?? null,
  labmap_status: data?.labmap_status ?? data?.status ?? null,
});

// เหตุผลของ needsReview จาก error
const getNeedsReviewReasons = (err: any): string[] | null => {
  if (!JSON.stringify(err ?? "").includes(PPTX_STATUS_NEEDS_REVIEW)) {
    return null;
  }

  const reasons = err?.data?.reviewReasons ?? err?.detail?.reviewReasons ?? err?.reviewReasons;

  return ([] as any[])
    .concat(reasons ?? [])
    .map((reason) =>
      typeof reason === "string" ? reason : reason?.message || JSON.stringify(reason),
    );
};

const FetchMeLabPptxStatus: Handler = async (controller, params) => {
  const [resp, err] = await AIChatFastAPIRequestView.get<{ success: boolean; data: any }>({
    endpoint: `/medical-ocr/reports/${params.reportId}/melab-pptx-status`,
    apiToken: controller.apiToken,
  });

  if (!err) {
    const state = controller.getState();

    controller.setState({
      LabReportSequence: {
        ...state.LabReportSequence,
        meLabPptxStatus: {
          ...state.LabReportSequence?.meLabPptxStatus,
          [params.reportId]: normalizeMeLabPptxStatus(resp?.data),
        },
      },
    });
  }

  params.onSettled?.();
};

const HandleGenerateMeLabPptx: Handler = async (controller, params) => {
  const { buttonLoadKey: btnKey, card } = params;
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const reportId = await GetOrCreateMeLabReport(controller, params);

  if (!reportId) {
    return;
  }

  const [resp, err] = await AIChatFastAPIRequestView.post<{ success: boolean; data: any }>({
    endpoint: `/medical-ocr/reports/${reportId}/melab-pptx-generate-v6`,
    params: {
      save_to_bucket: true,
      ...(params.allowNeedsReview && { allow_needs_review: true }),
    },
    apiToken: controller.apiToken,
  });

  state = controller.getState();

  if (err) {
    const reasons = getNeedsReviewReasons(err);

    if (reasons && !params.allowNeedsReview && params.onNeedsReview) {
      controller.setState({
        buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
      });

      params.onNeedsReview(reasons);

      return;
    }

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: { ...state.errorMessage, [card]: { error: err } },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "SUCCESS" },
    LabReportSequence: {
      ...state.LabReportSequence,
      meLabPptxStatus: {
        ...state.LabReportSequence?.meLabPptxStatus,
        [reportId]: normalizeMeLabPptxStatus(resp?.data),
      },
    },
  });

  if (!params.reportId) {
    await FetchMeLabReport(controller, params.selectedLabOrderWorking?.id);
  }

  params.onSuccess?.();
};

const HandleApproveMeLabPptx: Handler = async (controller, params) => {
  const { buttonLoadKey: btnKey, card } = params;
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const [resp, err] = await AIChatFastAPIRequestView.post<{ success: boolean; data: any }>({
    endpoint: `/medical-ocr/reports/${params.reportId}/approve-pptx`,
    apiToken: controller.apiToken,
  });

  state = controller.getState();

  if (err) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: { ...state.errorMessage, [card]: { error: err } },
    });

    return;
  }

  const meLabPptxStatus = state.LabReportSequence?.meLabPptxStatus;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
    LabReportSequence: {
      ...state.LabReportSequence,
      meLabPptxStatus: {
        ...meLabPptxStatus,
        [params.reportId]: normalizeMeLabPptxStatus({
          ...meLabPptxStatus?.[params.reportId],
          ...resp?.data,
        }),
      },
    },
  });

  params.onSuccess?.();
};

const normalizeMeLabVideoStatus = (data: any): MeLabVideoStatus => ({
  status: data?.generation_status_text || "",
  current_step: data?.current_step ?? null,
  current_step_detail: data?.current_step_detail ?? null,
  // backend ส่งมาเป็น string — ปล่อยไว้จะไปเทียบ id กับ project ที่เป็น number ไม่ติด
  megen_project_id: Number(data?.megen_project_id) || null,
  thread_id: data?.thread_id ?? null,
});

const FetchMeLabVideoStatus: Handler = async (controller, params) => {
  const [resp, err, network] = await AIChatFastAPIRequestView.get<{
    success: boolean;
    message: string;
    data: any;
  }>({
    endpoint: `/medical-ocr/reports/${params.reportId}/video-status`,
    apiToken: controller.apiToken,
  });

  const state = controller.getState();

  const saveStatus = (status: MeLabVideoStatus) => {
    controller.setState({
      LabReportSequence: {
        ...state.LabReportSequence,
        meLabVideoStatus: { ...state.LabReportSequence?.meLabVideoStatus, [params.reportId]: status },
      },
    });
  };

  if (err || !resp) {
    if (network?.response?.status === 404) {
      saveStatus({
        status: VIDEO_STATUS_NOT_FOUND,
        current_step: null,
        current_step_detail: null,
        megen_project_id: null,
        thread_id: null,
      });
    }

    params.onSettled?.();

    return;
  }

  saveStatus(normalizeMeLabVideoStatus(resp.data));

  params.onSettled?.();
};

// เปิดจอ run ของ MeGen เป็นโมดัลคร่อมหน้าเดิม (MainHIS เป็นคน render เพราะถือ state ของ MeGen ครบ)
const HandleGotoMeGenProject: Handler = (controller, params) => {
  if (!params.projectId) {
    return;
  }

  controller.setState({
    goToMeGen: { projectId: params.projectId, threadId: params.threadId },
  });
};

const FetchMeLabReport = async (controller: any, centralLabOrderId?: number | string) => {
  if (!centralLabOrderId) return;

  const [resp, err] = await AIChatFastAPIRequestView.get<MeLabReportSummary[]>({
    endpoint: "/medical-ocr/reports",
    params: { limit: 1, offset: 0, central_lab_order_id: String(centralLabOrderId) },
    apiToken: controller.apiToken,
  });

  if (err) return;

  const state = controller.getState();

  controller.setState({
    LabReportSequence: {
      ...state.LabReportSequence,
      meLabReport: resp?.[0] || null,
    },
  });
};

export const HandlePrintLabResult: Handler = async (controller, params) => {
  if (CONFIG.COMPANY === "MEHEALTH") {
    await HandlePrintMeLabResult(controller, params);
    return;
  }

  const state = controller.getState();
  const list = state.labOrderQueue?.labOrderQueueList || [];
  const { id, arrive_time: arriveTime, date } = params.selectedLabOrder || {};

  const docDef: any = await FormLaboratoryTestResult({
    arriveTime,
    labResultList: params.data,
    parentLabID: params.data.map((item: any) => item.code),
    patient: params.selectedPatient || {},
    receivedDatetime: arriveTime,
    requestedBy: list.find((item) => item.id === id)?.order_by_name || "",
    requestedDatetime: date,
  });

  (await getPdfMake()).createPdf(docDef).open();
};

const flattenLabItems = (items: any[]) =>
  items.flatMap((item) => (item.children?.length ? item.children : [item]));

const getReporterInfo = (items: any[]) => {
  let reportedBy = "";
  let reportedByLicence = "";
  let reportedDt = "";
  let authorizedBy = "";
  let authorizedByLicence = "";
  let authorizedDt = "";

  for (const target of flattenLabItems(items)) {
    if (!reportedBy && target.central_lab_result_report_user_fullname) {
      reportedBy = target.central_lab_result_report_user_fullname;
      reportedByLicence = target.central_lab_result_report_user_licence || "";
      reportedDt =
        target.central_lab_result_report_datetime || target.central_lab_result_edited || "";
    }
    if (!authorizedBy && target.central_lab_result_authorize_user_fullname) {
      authorizedBy = target.central_lab_result_authorize_user_fullname;
      authorizedByLicence = target.central_lab_result_authorize_user_licence || "";
      authorizedDt =
        target.central_lab_result_authorize_datetime || target.central_lab_result_edited || "";
    }
    if (reportedBy && authorizedBy) break;
  }

  return {
    reportedBy,
    reportedByLicence,
    reportedDate: reportedDt ? moment(reportedDt).format("DD MMM YYYY") : "",
    reportedTime: reportedDt ? moment(reportedDt).format("HH:mm") : "",
    authorizedBy,
    authorizedByLicence,
    authorizedDate: authorizedDt ? moment(authorizedDt).format("DD MMM YYYY") : "",
    authorizedTime: authorizedDt ? moment(authorizedDt).format("HH:mm") : "",
  };
};

const toFormItem = (src: any, extra?: Partial<FormMeLabResultProps["items"][0]>) => ({
  name: src.name,
  name_en: src.name_en,
  value: src.central_lab_result_value || "",
  unit: src.unit || "",
  refValue: src.ref_value_txt || "",
  flag: ["L", "H"].includes(src.central_lab_result_flag_result_label)
    ? src.central_lab_result_flag_result_label
    : "",
  ...extra,
});

const buildFormItems = (
  items: any[],
  divisionGroups: DivisionGroup[] = [],
): FormMeLabResultProps["items"] => {
  const seqMap = new Map(divisionGroups.map((d) => [d.id, d.display_seq]));
  const groups: Record<string, { seq: number; items: any[] }> = {};

  for (const item of items) {
    const key = item.lab_division_name || "";
    if (!groups[key]) {
      const seq = item.lab_division != null ? (seqMap.get(item.lab_division) ?? 999) : 999;
      groups[key] = { seq, items: [] };
    }
    groups[key].items.push(item);
  }

  return Object.entries(groups)
    .sort(([, a], [, b]) => a.seq - b.seq)
    .flatMap(([divName, group]) => [
      ...(divName
        ? [
            {
              name: divName,
              name_en: "",
              value: "",
              unit: "",
              refValue: "",
              flag: "",
              isDivisionHeader: true,
            },
          ]
        : []),
      ...group.items.flatMap((item: any) =>
        item.children?.length
          ? [
              {
                name: item.name,
                name_en: item.name_en,
                value: "",
                unit: "",
                refValue: "",
                flag: "",
                isParentHeader: true,
              },
              ...item.children.map((c: any) => toFormItem(c, { isChild: true })),
            ]
          : [toFormItem(item)],
      ),
    ]);
};

const separateConfidential = (labItems: any[]) => {
  const normal: any[] = [];
  const confidential: any[] = [];

  for (const item of labItems) {
    if (item.is_confidential) {
      confidential.push(item);
    } else if (item.children?.length) {
      const conf = item.children.filter((c: any) => c.is_confidential);
      const nonConf = item.children.filter((c: any) => !c.is_confidential);
      if (nonConf.length) normal.push({ ...item, children: nonConf });
      if (conf.length) confidential.push({ ...item, children: conf });
    } else {
      normal.push(item);
    }
  }

  return { normal, confidential };
};

const getLatestReportedDatetime = (items: any[]) =>
  flattenLabItems(items).reduce((latest, t) => {
    const dt = t.central_lab_result_report_datetime || t.central_lab_result_edited || "";
    return dt > latest ? dt : latest;
  }, "");

const HandlePrintMeLabResult: Handler = async (controller, params) => {
  console.log("Debug ~ HandlePrintMeLabResult ~ params:", params);
  const patient = params.selectedPatient || {};
  const selectedLabOrder = params.selectedLabOrder || {};
  const labItems: any[] = params.data || [];
  const encounterId = selectedLabOrder?.encounter;

  const labDivision = controller.data?.masterData?.labDivision || [];

  const oaLogoBase64 = await GetOALogoBase64(controller);
  const arriveTime = selectedLabOrder.arrive_time_iso || selectedLabOrder.arrive_time || "";
  const labNo = selectedLabOrder.full_access_number || String(selectedLabOrder.id || "");
  const reportedDatetime = getLatestReportedDatetime(labItems);
  const { normal, confidential } = separateConfidential(labItems);
  const pdfMake = await getPdfMake();

  const generatePdf = async (items: any[], isConfidential: boolean) => {
    const docDef: any = await FormMeLabResult({
      items: buildFormItems(items, labDivision),
      patient,
      labNo,
      collectedDatetime: arriveTime,
      receivedDatetime: arriveTime,
      reportedDatetime,
      ...getReporterInfo(items),
      isConfidential,
      remark: selectedLabOrder?.result_remark || "",
      printedBy: controller.data.userProfile?.full_name || "",
      logoImage: oaLogoBase64 || undefined,
      reportPdfLink: `${CONFIG.LINE_LIFF_URL}?oa=${controller.data.division}&labResult=true&packageOrder=${params?.packageOrderId || ""}`,
    });

    const dobAD = formatBirthdateAD(params.selectedPatient?.birthdate);

    const dobFormat = moment(dobAD, "DD/MM/YYYY");

    const pdfPassword = dobFormat.isValid()
      ? dobFormat.format("MMYYYY")
      : params.selectedPatient?.hn;

    pdfMake
      .createPdf({
        ...docDef,
        userPassword: pdfPassword, // Required to open
        ownerPassword: params.selectedPatient?.hn + params.selectedPatient.patient, // Allows full access
        permissions: {
          printing: "highResolution", // or 'lowResolution', 'none'
          modifying: false,
          copying: false,
        },
      })
      .open();

    // pdfMake.createPdf(docDef).open();
  };

  if (normal.length) {
    await generatePdf(normal, false);
  }
  if (confidential.length) {
    await generatePdf(confidential, true);
  }
};

// Utils
const getGroupByParentLab = (lists: any[]) => {
  const group = lists.reduce((result, item) => {
    const mapDates = (items: any[]) => {
      return items.map((item) => ({
        ...item,
        dates: [{ value: item.value, status: item.result_status }],
      }));
    };
    const children = item.children?.length ? [item, ...mapDates(item.children)] : mapDates([item]);

    result[item.code] = {
      id: item.id,
      items: children.map((acc: any) => ({
        authorize_user_fullname: acc.central_lab_result_authorize_user_fullname,
        dates: acc.dates,
        name: acc.name,
        ref_value: acc.ref_value_txt,
        report_user_fullname: acc.central_lab_result_report_user_fullname,
        unit: acc.unit,
      })),
    };

    return result;
  }, {} as any);

  return lists.length ? group : { default: { id: null, items: [] } };
};

// APIs
const GetEmployeeCode: Handler = async (controller, params) => {
  const { userId } = await controller.handleEvent({ message: "CheckUserCredentials", params });

  if (!userId) {
    return null;
  }

  const [employee] = await UserEmployeeDetailAPIView.retrieve({
    apiToken: controller.apiToken,
    pk: userId,
    extra: { division: controller.data.division },
  });

  return (employee?.code || "") as string;
};

export const GetCentralLabResultByOrderGroupbyTest: Handler = (controller, params) =>
  CentralLabResultByOrderGroupbyTestList.get({
    apiToken: controller.apiToken,
    params: {
      ab_type: "OUTLAB",
      after_collected: true,
      order_id: params.id,
    },
  }) as Promise<[unknown, unknown]>;

const formatBirthdateAD = (birthdate: string | undefined | null): string => {
  if (!birthdate) return "";

  const m = convertAdBeDateToMoment(birthdate);
  if (!m?.isValid()) return "";

  return m.format("DD/MM/YYYY");
};

export const HandleSaveResultRemark: Handler = async (controller, params) => {
  const state = controller.getState();

  const { buttonLoadKey: btnKey, card } = params;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const [centralLabOrderRes, centralLabOrderErr, centralLabOrderNet] = await CentralLabOrderDetail.patch({
    pk: state.selectedLabOrderWorking.id,
    data: {
      action: "EDIT",
      result_remark:  params.resultRemark || "",
    },
    apiToken: controller.apiToken,
  });

  if (centralLabOrderRes) {
    const resultRemark = centralLabOrderRes.result_remark || "";

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "SUCCESS" },
      selectedLabOrderWorking: { ...state.selectedLabOrderWorking, ...centralLabOrderRes },
      labOrderQueue: {
        ...state.labOrderQueue,
        labOrderQueueList: (state.labOrderQueue?.labOrderQueueList || []).map((item: any) =>
          item.id === state.selectedLabOrderWorking?.id
            ? { ...item, result_remark: resultRemark }
            : item
        ),
      },
    });

    params.onSuccess?.(false);
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "ERROR" },
      errorMessage: {
        ...state.errorMessage,
        [card]: { error: centralLabOrderErr || centralLabOrderNet },
      },
    });
  }
};
